import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { CompanySidebar } from "./CompanySidebar";
import { CompanyAccountButton } from "./CompanyAccountButton";
import { CompanyTipBubble } from "./CompanyTipBubble";

interface CompanyLayoutProps {
  children: ReactNode;
}

const SIDEBAR_PREF_KEY = "cw:sidebar-open";

/** Split-view detailpagina's met een eigen zijkolom: sidebar automatisch inklappen. */
const isSplitViewPath = (pathname: string) =>
  /^\/kandidaten\/[^/]+/.test(pathname) ||
  /^\/talentradar\/[^/]+/.test(pathname) ||
  /^\/bedrijf\/matches\/[^/]+/.test(pathname) ||
  /^\/bedrijf\/ai-recruiter\/[^/]+/.test(pathname) ||
  /^\/bedrijf\/vacature-boost(\/|$)/.test(pathname);

const readPreference = (): boolean => {
  if (typeof window === "undefined") return true;
  return sessionStorage.getItem(SIDEBAR_PREF_KEY) !== "false";
};

/** Bewaart de handmatig gekozen staat, maar alleen buiten de split-view pagina's. */
function SidebarPreferenceSync() {
  const { pathname } = useLocation();
  const { open, setOpen, isMobile } = useSidebar();

  // Bij navigeren binnen deze layout: inklappen op split-view, anders voorkeur herstellen.
  useEffect(() => {
    if (isMobile) return;
    setOpen(isSplitViewPath(pathname) ? false : readPreference());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isMobile]);

  useEffect(() => {
    if (isMobile || isSplitViewPath(pathname)) return;
    sessionStorage.setItem(SIDEBAR_PREF_KEY, String(open));
  }, [open, pathname, isMobile]);

  return null;
}

export function CompanyLayout({ children }: CompanyLayoutProps) {
  const { pathname } = useLocation();
  const defaultOpen = isSplitViewPath(pathname) ? false : readPreference();

  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <SidebarPreferenceSync />
      <div className="min-h-screen flex w-full bg-background overflow-x-hidden">
        <CompanySidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-12 sm:h-14 border-b bg-white/80 backdrop-blur flex items-center px-3 sm:px-4 gap-2 shrink-0 sticky top-0 z-30">
            <SidebarTrigger className="h-9 w-9 sm:h-7 sm:w-7" />
            <div className="ml-auto flex items-center gap-1 sm:gap-2">
              <CompanyAccountButton />
            </div>
          </header>
          <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
        </div>
        <CompanyTipBubble />
      </div>
    </SidebarProvider>
  );
}
